/*
  Studio Pro By Charlot · lecture et controle du fichier des destinataires.

  Le client depose un fichier Excel ou CSV. Tout se passe dans son navigateur :
  on retrouve les colonnes quel que soit leur nom ou leur ordre, on repare ce
  qu'Excel abime (le zero des codes postaux 01 a 09, le zero des portables),
  et on signale ligne par ligne ce qui empecherait une livraison.

  Une erreur n'empeche pas d'envoyer la demande de devis : le fichier n'est
  obligatoire qu'avant l'expedition. Elle est montree pour etre corrigee.

  Deux usages (30/09/2026) : les adresses d'une livraison en differents points, ou
  seulement le texte de chaque etiquette quand tout part a une seule adresse
  (option { sansAdresse: true } : ni adresse, ni code postal, ni ville exiges).
  Colonnes « Titre de l'etiquette » et « Sous-titre de l'etiquette » : vides, le
  texte commun choisi sur la page s'applique, ce n'est jamais une erreur.

  Necessite SheetJS (XLSX) pour lire les fichiers ; les fonctions de controle
  (controler, versCSV) fonctionnent seules.
*/
(function (racine, fabrique) {
  if (typeof module === "object" && module.exports) module.exports = fabrique();
  else racine.StudioAdresses = fabrique();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  const TAILLE_MAX = 10 * 1024 * 1024;
  const LIGNES_MAX = 5000;
  const LONGUEUR_ADRESSE = 35;       // au-dela, le transporteur coupe la ligne
  const LONGUEUR_TITRE = 30;         // au-dela, a verifier sur le bon a tirer
  const LONGUEUR_SOUS = 60;

  // Les noms de colonnes qu'on reconnait, deja normalises (sans accent, minuscules)
  const COLONNES = {
    prenom: ["prenom", "firstname", "first name", "given name"],
    nom: ["nom", "nom de famille", "lastname", "last name", "surname", "family name"],
    destinataire: ["destinataire", "nom du destinataire", "nom destinataire", "nom complet", "full name", "name", "nom et prenom", "prenom et nom", "prenom nom", "nom prenom", "contact"],
    societe: ["societe", "entreprise", "company", "raison sociale", "organisation", "nom de la societe", "nom societe", "nom de l entreprise", "nom entreprise", "company name"],
    adresse: ["adresse", "adresse 1", "adresse1", "adresse ligne 1", "address", "address 1", "address1", "address line 1", "rue", "numero et rue", "voie", "adresse postale"],
    complement: ["complement", "complement d adresse", "complement adresse", "adresse 2", "adresse2", "adresse ligne 2", "address 2", "address2", "address line 2", "batiment", "etage"],
    cp: ["code postal", "codepostal", "cp", "postal code", "postcode", "zip", "zip code", "zipcode"],
    ville: ["ville", "city", "commune", "localite", "town"],
    pays: ["pays", "country"],
    tel: ["telephone portable", "tel portable", "portable", "mobile", "telephone", "tel", "phone", "numero de telephone", "gsm", "mobile phone"],
    email: ["email", "e mail", "mail", "courriel", "adresse email", "adresse mail", "adresse e mail"],
    // jamais « titre » seul : dans un fichier d'adresses, c'est souvent la civilite
    sousTitre: ["sous titre de l etiquette", "sous titre etiquette", "sous titre", "soustitre", "etiquette sous titre", "sous titre de l etiquette facultatif"],
    titre: ["titre de l etiquette", "titre etiquette", "etiquette titre", "texte etiquette", "texte de l etiquette", "etiquette", "texte", "message etiquette", "prenom etiquette", "personnalisation"],
    cadeau: ["cadeau", "ligne", "reference cadeau", "ref cadeau", "plante"],
    pieces: ["pieces", "nombre de pieces", "quantite", "qte", "nb", "nombre"]
  };
  const LIBELLES = {
    prenom: "Prénom", nom: "Nom", destinataire: "Destinataire", societe: "Société", adresse: "Adresse",
    complement: "Complément", cp: "Code postal", ville: "Ville", pays: "Pays", tel: "Téléphone",
    email: "Email", titre: "Titre de l'étiquette", sousTitre: "Sous-titre de l'étiquette", cadeau: "Cadeau", pieces: "Pièces"
  };
  const NOMS = ["prenom", "nom", "destinataire", "societe"];
  const PAYS = {
    "france": "France", "fr": "France", "fra": "France", "france metropolitaine": "France",
    "monaco": "Monaco", "mc": "Monaco", "belgique": "Belgique", "belgium": "Belgique", "be": "Belgique",
    "suisse": "Suisse", "switzerland": "Suisse", "ch": "Suisse", "luxembourg": "Luxembourg", "lu": "Luxembourg"
  };

  function normaliser(s) {
    return String(s == null ? "" : s).normalize("NFD").replace(/[̀-ͯ]/g, "")
      .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  }

  function texte(v) {
    if (v == null) return "";
    if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(v).replace(".", ",");
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    return String(v).replace(/\s+/g, " ").trim();
  }

  // --------------------------------------------------------------- en-tetes
  function reconnaitre(cellule) {
    const c = normaliser(cellule);
    if (!c) return null;
    for (const [cle, noms] of Object.entries(COLONNES)) if (noms.includes(c)) return cle;
    // sinon le synonyme qui arrive le plus tot dans l'en-tete, le plus long a egalite :
    // « Code postal du destinataire » est un code postal, pas un destinataire
    let meilleur = null, position = Infinity, longueur = 0;
    const cadre = " " + c + " ";
    for (const [cle, noms] of Object.entries(COLONNES)) {
      for (const n of noms) {
        const p = n.length >= 3 ? cadre.indexOf(" " + n + " ") : -1;
        if (p >= 0 && (p < position || (p === position && n.length > longueur))) { meilleur = cle; position = p; longueur = n.length; }
      }
    }
    return meilleur;
  }

  // sansAdresse : un nom ou un texte d'etiquette suffit a reconnaitre l'en-tete
  function trouverEntete(rangees, sansAdresse) {
    let meilleure = null;
    for (let i = 0; i < Math.min(rangees.length, 15); i++) {
      const plan = {};
      (rangees[i] || []).forEach((cel, j) => {
        const cle = reconnaitre(cel);
        if (cle && plan[cle] === undefined) plan[cle] = j;
      });
      const score = Object.keys(plan).length;
      const valable = sansAdresse
        ? NOMS.concat(["titre", "sousTitre"]).some(k => plan[k] !== undefined)
        : score >= 3 && (plan.adresse !== undefined || plan.cp !== undefined);
      if (valable && (!meilleure || score > meilleure.score)) meilleure = { ligne: i, plan, score };
    }
    return meilleure;
  }

  // --------------------------------------------------------------- controles
  // regles de Charles : livraison en France metropolitaine seulement (22/09) ; depuis le 30/09 la Corse
  // (20, 2A, 2B) n'est plus livree. Hors zone (horsZone) : jamais bloquant, la demande passe « a confirmer ».
  // Le message ne nomme jamais la Corse.
  const HORS_ZONE = "Hors zone de livraison : passez par « Projet sur mesure »";
  function controlerCP(brut, pays) {
    let cp = texte(brut).replace(/\s/g, "").toUpperCase();
    const r = { cp, erreur: null, alerte: null, repare: false, horsZone: false };
    if (!cp) { r.erreur = "Code postal manquant"; return r; }
    if (pays !== "France") { r.erreur = `Hors France métropolitaine (${pays}) : passez par « Projet sur mesure »`; r.horsZone = true; return r; }
    if (/^2[AB]\d{0,3}$/.test(cp)) { r.erreur = HORS_ZONE; r.horsZone = true; return r; }
    if (/^\d{4}$/.test(cp)) { cp = "0" + cp; r.repare = true; }
    r.cp = cp;
    if (!/^\d{5}$/.test(cp)) { r.erreur = "Code postal invalide"; return r; }
    if (/^9[78]/.test(cp)) { r.erreur = (cp.startsWith("980") ? "Monaco" : "Outre-mer") + " : hors France métropolitaine, passez par « Projet sur mesure »"; r.horsZone = true; }
    else if (/^20/.test(cp)) { r.erreur = HORS_ZONE; r.horsZone = true; }
    return r;
  }

  function controlerTel(brut, pays) {
    const t = texte(brut);
    const r = { tel: t, erreur: null, alerte: null };
    if (!t) { r.alerte = "Téléphone manquant, conseillé pour le livreur"; return r; }
    let d = t.replace(/[^\d+]/g, "");
    if (pays === "France") {
      if (d.startsWith("+33")) d = "0" + d.slice(3).replace(/^0/, "");
      else if (d.startsWith("0033")) d = "0" + d.slice(4).replace(/^0/, "");
      else if (/^33\d{9}$/.test(d)) d = "0" + d.slice(2);
      else if (/^[1-9]\d{8}$/.test(d)) d = "0" + d;
      if (!/^0[1-9]\d{8}$/.test(d)) { r.erreur = "Téléphone invalide"; return r; }
      r.tel = d.replace(/(\d{2})(?=\d)/g, "$1 ");
      if (!/^0[67]/.test(d)) r.alerte = "Numéro fixe : un portable est préférable";
      return r;
    }
    if (d.replace(/\D/g, "").length < 8) r.erreur = "Téléphone invalide";
    return r;
  }

  function controlerLigne(v, numero, options) {
    const sansAdresse = !!(options && options.sansAdresse);
    const erreurs = [], alertes = [], reparations = [];
    const pays = PAYS[normaliser(v.pays)] || (v.pays ? texte(v.pays) : "France");
    const sortie = {
      prenom: texte(v.prenom), nom: texte(v.nom), destinataire: texte(v.destinataire), societe: texte(v.societe),
      adresse: texte(v.adresse), complement: texte(v.complement), ville: texte(v.ville), pays,
      email: texte(v.email), titre: texte(v.titre), sousTitre: texte(v.sousTitre), cadeau: texte(v.cadeau).toUpperCase(), pieces: 1
    };
    sortie.texte = [sortie.titre, sortie.sousTitre].filter(Boolean).join(" / ");     // l'ancienne colonne unique
    if (!sortie.nom && !sortie.prenom && sortie.destinataire) sortie.nom = sortie.destinataire;
    // a une seule adresse, un texte d'etiquette suffit a faire une ligne
    if (!sortie.nom && !sortie.prenom && !sortie.societe) { if (!sansAdresse || !sortie.texte) erreurs.push("Nom du destinataire manquant"); }
    else if (!sortie.nom && !sortie.prenom && !sansAdresse) alertes.push("Pas de nom de personne, seulement la société");

    let horsZone = false;
    if (sansAdresse) { sortie.cp = texte(v.cp).replace(/\s/g, ""); sortie.tel = texte(v.tel); }
    else {
      if (!sortie.adresse) erreurs.push("Adresse manquante");
      else if (sortie.adresse.length > LONGUEUR_ADRESSE) alertes.push(`Adresse de plus de ${LONGUEUR_ADRESSE} caractères : à couper sur « Complément »`);

      const cp = controlerCP(v.cp, pays);
      sortie.cp = cp.cp; horsZone = cp.horsZone;
      if (cp.erreur) erreurs.push(cp.erreur);
      if (cp.alerte) alertes.push(cp.alerte);
      if (cp.repare) reparations.push("Code postal complété d'un zéro");
      if (!sortie.ville) erreurs.push("Ville manquante");

      const tel = controlerTel(v.tel, pays);
      sortie.tel = tel.tel;
      if (tel.erreur) erreurs.push(tel.erreur);
      if (tel.alerte) alertes.push(tel.alerte);
    }

    if (sortie.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(sortie.email)) alertes.push("Email invalide");
    // vides : le texte commun choisi sur la page, jamais une erreur
    if (sortie.titre.length > LONGUEUR_TITRE) alertes.push("Titre d'étiquette long : à vérifier sur le bon à tirer");
    if (sortie.sousTitre.length > LONGUEUR_SOUS) alertes.push("Sous-titre d'étiquette long : à vérifier sur le bon à tirer");

    const p = texte(v.pieces);
    if (p) {
      if (/^\d{1,4}$/.test(p) && +p >= 1) sortie.pieces = +p;
      else erreurs.push("Nombre de pièces invalide");
    }
    return { numero, valeurs: sortie, erreurs, alertes, reparations, horsZone };
  }

  /*
    rangees : tableau de tableaux (premiere feuille utile du fichier)
    renvoie { ok, fatal, lignes, envois, pieces, nbErreurs, nbAlertes, colonnes, manquantes, ligneEntete }
  */
  // decalage : numero de la premiere rangee dans Excel moins un, pour que les numeros
  // de ligne affiches soient ceux que le client voit dans son fichier
  // options.sansAdresse : tout part a une seule adresse, le fichier ne porte que les noms et les textes d'etiquette
  function controler(rangees, decalage, options) {
    decalage = decalage || 0;
    const sansAdresse = !!(options && options.sansAdresse);
    const res = { ok: false, fatal: null, lignes: [], envois: 0, pieces: 0, nbErreurs: 0, nbAlertes: 0, colonnes: [], manquantes: [], ligneEntete: null,
      sansAdresse, horsZone: 0, nbTextes: 0, colonnesTexte: false };
    // en differents points, un fichier fait pour les seuls textes (noms, titres) a bien des en-tetes : on dit ce qui manque
    const entete = trouverEntete(rangees || [], sansAdresse) || (sansAdresse ? null : trouverEntete(rangees || [], true));
    if (!entete) {
      res.sansEntete = true;
      res.fatal = "Nous ne trouvons pas les en-têtes du fichier. La première ligne doit nommer les colonnes : " +
        (sansAdresse ? "Prénom, Nom, Titre de l'étiquette, Sous-titre de l'étiquette." : "Prénom, Nom, Adresse, Code postal, Ville, Téléphone.");
      return res;
    }
    const plan = entete.plan;
    res.ligneEntete = entete.ligne + 1 + decalage;
    res.colonnes = Object.keys(plan).map(k => LIBELLES[k]);
    res.colonnesTexte = plan.titre !== undefined || plan.sousTitre !== undefined;
    const aUnNom = NOMS.some(k => plan[k] !== undefined);
    if (!aUnNom && !(sansAdresse && res.colonnesTexte)) res.manquantes.push("Nom");
    if (!sansAdresse) ["adresse", "cp", "ville"].forEach(k => { if (plan[k] === undefined) res.manquantes.push(LIBELLES[k]); });
    if (res.manquantes.length) {
      res.fatal = `Colonne${res.manquantes.length > 1 ? "s" : ""} introuvable${res.manquantes.length > 1 ? "s" : ""} : ${res.manquantes.join(", ")}.`;
      return res;
    }

    const vus = new Map();
    for (let i = entete.ligne + 1; i < rangees.length; i++) {
      const r = rangees[i] || [];
      if (!r.some(c => texte(c) !== "")) continue;                  // ligne vide
      const v = {};
      for (const [cle, j] of Object.entries(plan)) v[cle] = r[j];
      const ligne = controlerLigne(v, i + 1 + decalage, options);
      const val = ligne.valeurs, qui = normaliser(val.prenom + " " + val.nom);
      // a une seule adresse, un doublon est le meme nom avec le meme texte (deux « Camille » peuvent coexister)
      const cle = sansAdresse ? (qui ? [qui, normaliser(val.titre), normaliser(val.sousTitre)].join("|") : "") : [qui, normaliser(val.adresse), val.cp].join("|");
      if (cle && vus.has(cle)) ligne.alertes.push(`Doublon de la ligne ${vus.get(cle)}`);
      else if (cle) vus.set(cle, ligne.numero);
      res.lignes.push(ligne);
      if (res.lignes.length > LIGNES_MAX) {
        res.fatal = `Plus de ${LIGNES_MAX} destinataires : envoyez-nous le fichier à business@bycharlot.com.`;
        return res;
      }
    }
    if (!res.lignes.length) { res.fatal = "Le fichier ne contient aucun destinataire sous les en-têtes."; return res; }

    res.envois = res.lignes.length;
    res.pieces = res.lignes.reduce((s, l) => s + l.valeurs.pieces, 0);
    res.nbErreurs = res.lignes.filter(l => l.erreurs.length).length;
    res.nbAlertes = res.lignes.filter(l => !l.erreurs.length && l.alertes.length).length;
    res.horsZone = res.lignes.filter(l => l.horsZone).length;
    res.nbTextes = res.lignes.filter(l => l.valeurs.titre || l.valeurs.sousTitre).length;
    res.ok = true;
    return res;
  }

  // --------------------------------------------------------------- lecture
  function decoderTexte(octets) {
    if (octets[0] === 0xEF && octets[1] === 0xBB && octets[2] === 0xBF) return new TextDecoder("utf-8").decode(octets.subarray(3));
    if (octets[0] === 0xFF && octets[1] === 0xFE) return new TextDecoder("utf-16le").decode(octets.subarray(2));
    try { return new TextDecoder("utf-8", { fatal: true }).decode(octets); }
    catch (e) { return new TextDecoder("windows-1252").decode(octets); }
  }

  // le separateur le plus present sur les premieres lignes (la premiere peut etre une ligne d'aide)
  function separateur(txt) {
    const lignes = txt.split(/\r?\n/).filter(l => l.trim()).slice(0, 5);
    const compte = s => Math.max(0, ...lignes.map(l => l.split(s).length - 1));
    return [";", ",", "\t"].reduce((a, b) => (compte(b) > compte(a) ? b : a), ";");
  }

  // octets : Uint8Array ; nom : nom du fichier ; XLSX : la bibliotheque SheetJS ; options : { sansAdresse }
  function lire(octets, nom, XLSX, options) {
    if (!octets || !octets.length) return { ok: false, fatal: "Le fichier est vide." };
    if (octets.length > TAILLE_MAX) return { ok: false, fatal: "Le fichier dépasse 10 Mo." };
    const ext = (String(nom).toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || "";
    if (!["xlsx", "xls", "csv", "txt", "ods"].includes(ext)) return { ok: false, fatal: "Format non pris en charge : envoyez un fichier Excel (.xlsx, .xls) ou CSV." };

    let classeur;
    try {
      if (ext === "csv" || ext === "txt") {
        const txt = decoderTexte(octets);
        classeur = XLSX.read(txt, { type: "string", raw: true, FS: separateur(txt) });
      } else {
        classeur = XLSX.read(octets, { type: "array", cellFormula: false, cellHTML: false, cellDates: true });
      }
    } catch (e) {
      const m = String(e && e.message || e);
      if (/password|encrypt|protect/i.test(m)) return { ok: false, fatal: "Le fichier est protégé par un mot de passe : enregistrez-le sans protection." };
      return { ok: false, fatal: "Fichier illisible. Enregistrez-le au format Excel (.xlsx) ou CSV, puis réessayez." };
    }

    // la feuille des destinataires : celle qui porte ce nom, sinon la premiere qui a des en-tetes
    const noms = classeur.SheetNames || [];
    const ordre = noms.slice().sort((a, b) => (/destinat|adress|contact/i.test(b) ? 1 : 0) - (/destinat|adress|contact/i.test(a) ? 1 : 0));
    for (const n of ordre) {
      const feuille = classeur.Sheets[n];
      const debut = feuille && feuille["!ref"] ? XLSX.utils.decode_range(feuille["!ref"]).s.r : 0;
      // les lignes vides sont gardees : sinon les numeros ne correspondraient plus au fichier
      const rangees = feuille ? XLSX.utils.sheet_to_json(feuille, { header: 1, raw: true, defval: "", blankrows: true }) : [];
      const res = controler(rangees, debut, options);
      // on passe a la feuille suivante seulement si celle-ci n'a pas d'en-tetes du tout
      if (!res.sansEntete || n === ordre[ordre.length - 1]) {
        res.feuille = n;
        return res;
      }
    }
    return { ok: false, fatal: "Le fichier ne contient aucune feuille." };
  }

  // --------------------------------------------------------------- sortie
  // une cellule qui commence par = + - @ serait executee par Excel : on la neutralise
  function sure(v) {
    const s = String(v == null ? "" : v);
    return /^[=+\-@\t\r]/.test(s) ? "'" + s : s;
  }

  function versCSV(res) {
    const tete = ["N° ligne", "Prénom", "Nom", "Titre de l'étiquette", "Sous-titre de l'étiquette", "Société", "Adresse", "Complément",
                  "Code postal", "Ville", "Pays", "Téléphone", "Email", "Cadeau", "Pièces", "Statut"];
    const q = v => { const s = sure(v); return /[;"\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const lignes = res.lignes.map(l => {
      const v = l.valeurs;
      const statut = l.erreurs.length ? "À corriger : " + l.erreurs.join(", ") : l.alertes.length ? "À vérifier : " + l.alertes.join(", ") : "OK";
      return [l.numero, v.prenom, v.nom, v.titre, v.sousTitre, v.societe, v.adresse, v.complement, v.cp, v.ville, v.pays, v.tel, v.email, v.cadeau, v.pieces, statut].map(q).join(";");
    });
    return "﻿" + [tete.join(";")].concat(lignes).join("\r\n") + "\r\n";
  }

  /*
    Le fichier propre transmis a la personne qui prepare les expeditions : une ligne par
    colis, colonnes fixes, code postal et telephone en texte (le zero ne peut plus sauter).
    Les cellules sont des textes, jamais des formules : rien ne s'execute a l'ouverture.
  */
  function versXLSX(res, XLSX, entete) {
    const tete = ["N° ligne client", "Prénom", "Nom", "Titre de l'étiquette", "Sous-titre de l'étiquette", "Société", "Adresse", "Complément",
                  "Code postal", "Ville", "Pays", "Téléphone", "Email", "Cadeau", "Pièces", "Statut"];
    const aoa = [tete].concat(res.lignes.map(l => {
      const v = l.valeurs;
      const statut = l.erreurs.length ? "À corriger : " + l.erreurs.join(", ") : l.alertes.length ? "À vérifier : " + l.alertes.join(", ") : "OK";
      return [l.numero, v.prenom, v.nom, v.titre, v.sousTitre, v.societe, v.adresse, v.complement, v.cp, v.ville, v.pays, v.tel, v.email, v.cadeau, v.pieces, statut];
    }));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    for (let i = 1; i < aoa.length; i++) {
      for (const col of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15]) {
        const ref = XLSX.utils.encode_cell({ r: i, c: col });
        if (ws[ref]) { ws[ref].t = "s"; ws[ref].v = String(ws[ref].v); delete ws[ref].f; }
      }
    }
    ws["!cols"] = [8, 14, 16, 22, 26, 18, 30, 20, 11, 18, 10, 15, 24, 8, 7, 44].map(w => ({ wch: w }));
    ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: aoa.length - 1, c: tete.length - 1 } }) };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Envois");
    if (entete) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(entete), "Demande");
    return XLSX.write(wb, { bookType: "xlsx", type: "array", compression: true });
  }

  return { lire, controler, versXLSX, controlerLigne, controlerCP, controlerTel, reconnaitre, versCSV, normaliser, LIBELLES };
});
